import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import Select from "../../../../../components/Form/Select";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { CompanyProfile } from "../../../../../models/company.profile";
import { validateSecondStep } from "./validation";
import {
  inputSx,
  selectSx,
  backButtonSx,
  nextButtonSx,
  gridItem,
  buttonRow,
} from "./styles";
import { SIGNUP_COLORS } from "../../tokens";

type SecondStepForm = {
  companyName: string;
  companyInitials: string;
};

interface SecondStepProps {
  profiles?: CompanyProfile[];
  isLoading?: boolean;
  objectPreview?: SecondStepForm;
  handleNextStep?: () => void;
  handleSkipToStep?: (step: number) => void;
  onPreviousStep?: () => void;
}

export interface SecondStepHandle {
  companyName: string;
  companyType: string;
}

const SecondStep: ForwardRefRenderFunction<SecondStepHandle, SecondStepProps> = (
  props,
  ref
) => {
  const { profiles, isLoading, objectPreview, handleNextStep, handleSkipToStep, onPreviousStep } = props;
  const { t: translate } = useTranslation();

  const [companyName, setCompanyName] = useState<string>(objectPreview?.companyName ?? "");
  const [companyType, setCompanyType] = useState<string>(objectPreview?.companyInitials ?? "");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const formValues: SecondStepHandle = { companyName, companyType };
  useImperativeHandle(ref, () => formValues);

  const fieldBaseInfo = (name: string) => ({
    error: fieldHasError(name),
    errorMessage: getErrorByField(name),
    "data-cy": name,
    label: translate(name),
    placeholder: translate(name),
    "aria-label": name,
    name,
    bordercolor: SIGNUP_COLORS.primary,
  });

  const onNext = () => {
    setHasSubmitted(true);
    if (validateSecondStep(translate, formValues, setErrors)) {
      const selectedProfile = profiles?.find(p => p.initials === companyType);
      if (selectedProfile?.name === "Supply Chain") {
        handleSkipToStep?.(4);
      } else {
        handleNextStep?.();
      }
    }
  };

  useEffect(() => {
    if (objectPreview) {
      setCompanyName(objectPreview.companyName ?? "");
      setCompanyType(objectPreview.companyInitials ?? "");
    }
  }, [objectPreview]);

  useEffect(() => {
    if (hasSubmitted) validateSecondStep(translate, formValues, setErrors);
  }, [hasSubmitted, companyName, companyType, translate, formValues]);

  return (
    <>
      <Grid item xs={12} sx={{ mb: "4px" }}>
        <Input
          {...fieldBaseInfo("companyName")}
          required
          onChange={(e) => setCompanyName(e.target.value)}
          value={companyName}
          disabled={isLoading}
          sx={inputSx}
        />
      </Grid>

      <Grid item xs={12} sx={{ mb: 0 }}>
        <Select
          error={fieldHasError("companyType")}
          errorMessage={getErrorByField("companyType")}
          data-cy="companyType"
          label={translate("signup.right.selectYourPlatform")}
          placeholder="Select"
          aria-label="selectYourPlatform"
          name="companyType"
          bordercolor={SIGNUP_COLORS.primary}
          required
          value={companyType}
          onChange={(e: SelectChangeEvent<any>) => setCompanyType(e.target.value)}
          disabled={isLoading}
          sx={selectSx}
        >
          {profiles?.map((p) => (
            <MenuItem key={p.initials} value={p.initials}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} container spacing={0} sx={buttonRow}>
        <Grid item xs={12} sm={6} sx={{ pr: { sm: 1 } }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPreviousStep}
            disabled={isLoading}
            data-cy="backButton"
            aria-label="backButton"
            sx={backButtonSx}
          >
            {translate("back")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ pl: { sm: 1 } }}>
          <Button
            fullWidth
            customColor={Color.SECONDARY_DARK}
            onClick={onNext}
            disabled={!companyName || !companyType}
            loading={isLoading}
            data-cy="nextStepButton"
            sx={nextButtonSx}
          >
            {translate("next")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default forwardRef(SecondStep);
